import { Link } from 'react-router-dom';
import { isProduction } from '@/lib/api';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <nav className="px-6 lg:px-16 py-6 flex justify-between items-center bg-charcoal">
        <Link to="/" className="font-display text-2xl font-bold text-warm-white tracking-tight">
          venndela
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/login" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
            Log In
          </Link>
          <Link to={isProduction() ? '/coming-soon' : '/register'} className="btn-primary px-6 py-2 text-sm">
            Get Started
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">Privacy Policy</h1>
        <p className="text-charcoal/60 mb-12">Last updated: January 2025</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">1. Introduction</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              Venndela ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your information when you use our
              affiliate marketing platform.
            </p>
            <p className="text-charcoal/80 leading-relaxed">
              By using Venndela, you consent to the data practices described in this policy. If you do not
              agree with these practices, please do not use our Service.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">2. Information We Collect</h2>

            <h3 className="font-display text-xl font-bold text-charcoal mb-3">2.1 Information You Provide</h3>
            <p className="text-charcoal/80 leading-relaxed mb-4">We collect information you voluntarily provide, including:</p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2 mb-6">
              <li><strong>Account Information:</strong> Name, email address, password, organization name</li>
              <li><strong>Business Information:</strong> Business category, website URL, contact details</li>
              <li><strong>Payment Information:</strong> Billing address, payment method details (processed by Stripe)</li>
              <li><strong>Communication Data:</strong> Support requests, feedback, correspondence</li>
            </ul>

            <h3 className="font-display text-xl font-bold text-charcoal mb-3">2.2 Information Collected Automatically</h3>
            <p className="text-charcoal/80 leading-relaxed mb-4">When you use our Service, we automatically collect:</p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2 mb-6">
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the platform</li>
              <li><strong>Log Data:</strong> IP address (hashed for privacy), access times, error logs</li>
              <li><strong>Tracking Data:</strong> Click data, referral information, conversion events</li>
            </ul>

            <h3 className="font-display text-xl font-bold text-charcoal mb-3">2.3 Cookies and Tracking Technologies</h3>
            <p className="text-charcoal/80 leading-relaxed mb-4">We use cookies and similar technologies to:</p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li>Maintain your session and preferences</li>
              <li>Track affiliate referrals and attribution (via first-party cookies)</li>
              <li>Analyze usage patterns and improve the Service</li>
              <li>Prevent fraud and enhance security</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">3. How We Use Your Information</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">We use collected information to:</p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li>Provide, maintain, and improve the Service</li>
              <li>Process transactions and send related information</li>
              <li>Track referrals, conversions, and calculate commissions</li>
              <li>Send administrative notifications and updates</li>
              <li>Respond to comments, questions, and support requests</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, investigate, and prevent fraudulent or illegal activities</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">4. Information Sharing</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">We may share your information in the following circumstances:</p>

            <h3 className="font-display text-xl font-bold text-charcoal mb-3">4.1 With Organizations and Affiliates</h3>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              When affiliates join an organization's program, relevant performance data (clicks, conversions,
              commissions) is shared between parties to facilitate the affiliate relationship.
            </p>

            <h3 className="font-display text-xl font-bold text-charcoal mb-3">4.2 Service Providers</h3>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              We share information with third-party service providers who perform services on our behalf:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2 mb-6">
              <li><strong>Stripe:</strong> Payment processing and payouts</li>
              <li><strong>AWS:</strong> Cloud infrastructure and data storage</li>
              <li><strong>Email providers:</strong> Transactional and marketing communications</li>
              <li><strong>Analytics providers:</strong> Usage analysis and reporting</li>
            </ul>

            <h3 className="font-display text-xl font-bold text-charcoal mb-3">4.3 Legal Requirements</h3>
            <p className="text-charcoal/80 leading-relaxed">
              We may disclose information if required by law, regulation, legal process, or governmental request,
              or to protect rights, privacy, safety, or property.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">5. Data Retention</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">We retain your information as follows:</p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li><strong>Account data:</strong> Retained while account is active, plus 7 years for legal compliance</li>
              <li><strong>Transaction data:</strong> 7 years for tax and legal requirements</li>
              <li><strong>Click tracking data:</strong> Automatically deleted after 90 days (TTL)</li>
              <li><strong>Analytics data:</strong> Aggregated and anonymized after 2 years</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">6. Data Security</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              We implement appropriate technical and organizational measures to protect your information:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li>Encryption in transit (TLS/SSL) and at rest</li>
              <li>IP address hashing for click tracking</li>
              <li>Access controls and authentication requirements</li>
              <li>Regular security assessments and monitoring</li>
              <li>Secure payment processing through PCI-compliant Stripe</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">7. Your Rights and Choices</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">Depending on your location, you may have the following rights:</p>

            <div className="bg-tan/30 border border-tan rounded-lg p-6 mb-4">
              <h3 className="font-display font-bold text-charcoal mb-3">Data Subject Rights:</h3>
              <ul className="list-disc pl-6 text-charcoal/80 space-y-2 text-sm">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Request data in a portable format</li>
                <li><strong>Objection:</strong> Object to certain processing activities</li>
                <li><strong>Restriction:</strong> Request limitation of processing</li>
              </ul>
            </div>

            <p className="text-charcoal/80 leading-relaxed">
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@venndela.com" className="text-rust hover:text-rust-light">privacy@venndela.com</a>.
              We will respond within 30 days.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">8. International Data Transfers</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              Your information may be transferred to and processed in countries other than your own. We ensure
              appropriate safeguards are in place, including:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li>Standard Contractual Clauses for EU data transfers</li>
              <li>Data processing agreements with all service providers</li>
              <li>Compliance with applicable data protection laws</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">9. Children's Privacy</h2>
            <p className="text-charcoal/80 leading-relaxed">
              The Service is not intended for individuals under 18 years of age. We do not knowingly collect
              personal information from children. If you believe we have collected information from a minor,
              please contact us immediately.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">10. Third-Party Links</h2>
            <p className="text-charcoal/80 leading-relaxed">
              The Service may contain links to third-party websites. We are not responsible for the privacy
              practices of these external sites. We encourage you to review the privacy policies of any
              third-party sites you visit.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">11. California Privacy Rights (CCPA)</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              California residents have additional rights under the California Consumer Privacy Act:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to opt-out of the sale of personal information</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
            <p className="text-charcoal/80 leading-relaxed mt-4">
              <strong>Note:</strong> We do not sell personal information to third parties.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">12. GDPR Compliance (EU Users)</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              For users in the European Economic Area, we process data based on:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li><strong>Contract:</strong> To provide the Service you requested</li>
              <li><strong>Legitimate Interests:</strong> For analytics, security, and service improvement</li>
              <li><strong>Consent:</strong> For marketing communications (where applicable)</li>
              <li><strong>Legal Obligation:</strong> To comply with applicable laws</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">13. Changes to This Policy</h2>
            <p className="text-charcoal/80 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of material changes by
              email or through the Service. Your continued use after changes constitutes acceptance of the
              updated policy.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">14. Contact Us</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              For questions about this Privacy Policy or our data practices:
            </p>
            <ul className="text-charcoal/80 space-y-1">
              <li>Privacy inquiries: <a href="mailto:privacy@venndela.com" className="text-rust hover:text-rust-light">privacy@venndela.com</a></li>
              <li>General support: <a href="mailto:support@venndela.com" className="text-rust hover:text-rust-light">support@venndela.com</a></li>
              <li>Data protection officer: <a href="mailto:dpo@venndela.com" className="text-rust hover:text-rust-light">dpo@venndela.com</a></li>
            </ul>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-16 pt-8 border-t border-tan/30">
          <h3 className="font-display text-lg font-bold text-charcoal mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/legal/terms"
              className="text-rust hover:text-rust-light underline"
            >
              Terms of Service
            </Link>
            <Link
              to="/legal/acceptable-use"
              className="text-rust hover:text-rust-light underline"
            >
              Acceptable Use Policy
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-charcoal border-t border-white/10 py-12 px-6 lg:px-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="font-display text-xl font-bold text-warm-white">
            venndela
          </Link>
          <p className="text-charcoal/50 text-sm">
            © {new Date().getFullYear()} Venndela. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
